/**
 * TakedownDialog - asking a moderator why, before something disappears
 * ---------------------------------------------------------------------
 * Used for both chores and offers. The only difference between the two is
 * wording, so they share this rather than growing two dialogs that drift.
 *
 * The reason is required and is not a note-to-self: it is sent to the person
 * who posted the thing, word for word, as a notification. Something vanishing
 * from your own dashboard with no explanation reads as the site being broken,
 * and a decision nobody can understand is a decision nobody can argue with.
 *
 * The note about it being reversible is there on purpose. A moderator who
 * believes a takedown is permanent hesitates over the borderline cases, which
 * are exactly the ones where acting quickly and reviewing afterwards is the
 * right order. Saying so at the moment of the decision is what makes that
 * true in practice rather than only in the data model.
 */

import { useState } from 'react';
import { EyeOff } from 'lucide-react';

import Modal from './Modal';
import { useI18n } from '../hooks/useI18n';

export default function TakedownDialog({ open, kind, name, onClose, onConfirm, busy }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const noun = kind === 'offer' ? 'availability' : 'chore';

  const confirm = () => {
    const message = reason.trim();
    if (!message) {
      setError(t('takedown.needReason'));
      return;
    }
    setError('');
    onConfirm(message);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind === 'offer'
        ? t('takedown.titleOffer', { name: name || '' })
        : t('takedown.titleChore')}
      actions={
        <>
          <button type="button" className="btn btn-ghost btn-inline" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-danger btn-inline" onClick={confirm} disabled={busy}>
            <EyeOff size={16} />
            {busy ? t('takedown.takingDown') : t('takedown.takeItDown')}
          </button>
        </>
      }
    >
      <p className="text-muted">
        {kind === 'offer'
          ? t('takedown.effectOffer')
          : t('takedown.effectChore')}
      </p>

      <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
        <label htmlFor="takedown-reason">{t('takedown.whyTheyRead')}</label>
        <textarea
          id="takedown-reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder={
            kind === 'offer'
              ? t('takedown.hintOffer')
              : t('takedown.hintChore')
          }
        />
        <p className="hint">
          {t('takedown.reasonHint')}
        </p>
      </div>

      <div className="alert alert-info">
        {t('takedown.nothingDeletedBefore')}{' '}
        <strong>{t('takedown.takenDownTab')}</strong>{t('takedown.nothingDeletedAfter')}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
    </Modal>
  );
}
